import { Box, Button, List, ListSubheader, Typography } from '@mui/material';
import { Fragment, useEffect, useMemo } from 'react';
import { DAY_MS } from '@/common/constants';
import { BaseDrawer, drawerContentSx } from '../../../components/drawer/base_drawer.tsx';
import { useNotificationStore } from '../notifications_store.ts';
import type { PersistentNotification } from '../types.ts';
import { NotificationListItem } from './NotificationListItem.tsx';

interface NotificationsDrawerProps {
  open: boolean;
  onClose: () => void;
}

type NotificationGroup = Record<string, PersistentNotification[]>;

const groupOrder = ['Today (24H)', 'This Week', 'Last Week', 'This Month', 'Older'] as const;

const noNotificationsBoxSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  p: 3,
};

const groupSubheaderSx = {
  px: 2,
  lineHeight: '48px',
  color: 'text.secondary',
  fontWeight: 500,
  fontSize: '0.875rem',
  letterSpacing: '0.1px',
  bgcolor: 'transparent',
};

function getDateGroup(created: number): (typeof groupOrder)[number] | 'Older' {
  const diffMs = Date.now() - created;
  const diffDays = Math.floor(diffMs / DAY_MS);

  if (diffMs < DAY_MS) {
    return 'Today (24H)';
  }
  if (diffDays < 7) {
    return 'This Week';
  }
  if (diffDays < 14) {
    return 'Last Week';
  }
  if (diffDays < 30) {
    return 'This Month';
  }
  return 'Older';
}

function groupNotificationsByDate(notifications: PersistentNotification[]): NotificationGroup {
  return notifications.reduce<NotificationGroup>((groups, notification) => {
    const group = getDateGroup(notification.created);
    if (!groups[group]) {
      groups[group] = [];
    }
    groups[group].push(notification);
    return groups;
  }, {});
}

export function NotificationsDrawer({ open, onClose }: NotificationsDrawerProps) {
  const notifications = useNotificationStore((state) => state.list);
  const clearAll = useNotificationStore((state) => state.clear);
  const markAllRead = useNotificationStore((state) => state.markAllRead);
  const groupedNotifications = useMemo(() => groupNotificationsByDate(notifications), [notifications]);
  const orderedGroups = useMemo(() => groupOrder.filter((group) => groupedNotifications[group]), [groupedNotifications]);

  useEffect(() => {
    if (open) {
      markAllRead();
    }
  }, [open, markAllRead]);

  return (
    <BaseDrawer
      open={open}
      onClose={onClose}
      width={320}
      title="Notifications"
      closeAriaLabel="close notifications"
      headerAction={
        <Button variant="text" size="small" onClick={clearAll} disabled={notifications.length === 0}>
          Clear all
        </Button>
      }
    >
      <List sx={drawerContentSx} disablePadding>
        {notifications.length === 0 ? (
          <Box sx={noNotificationsBoxSx}>
            <Typography variant="body2" color="text.secondary">
              No notifications
            </Typography>
          </Box>
        ) : (
          orderedGroups.map((groupName) => (
            <Fragment key={groupName}>
              <ListSubheader disableSticky sx={groupSubheaderSx}>
                {groupName}
              </ListSubheader>
              {groupedNotifications[groupName]!.map((notification) => (
                <NotificationListItem key={notification.id} notification={notification} />
              ))}
            </Fragment>
          ))
        )}
      </List>
    </BaseDrawer>
  );
}
